/**
 * Copyright 2024-2026, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { InMemoryLruCache } from "../utils/cache/in_memory_lru_cache";
import { ExponentialBackoff, IntervalRepeater } from "../utils/repeater/repeater";
import { DefaultOdpEventApiManager } from "./event_manager/odp_event_api_manager";
import { DefaultOdpEventManager } from "./event_manager/odp_event_manager";
import { DefaultOdpManager } from "./odp_manager";
import { DefaultOdpSegmentApiManager } from "./segment_manager/odp_segment_api_manager";
import { DefaultOdpSegmentManager } from "./segment_manager/odp_segment_manager";
export const DEFAULT_CACHE_SIZE = 10000;
export const DEFAULT_CACHE_TIMEOUT = 600000;
export const DEFAULT_EVENT_BATCH_SIZE = 100;
export const DEFAULT_EVENT_FLUSH_INTERVAL = 10000;
export const DEFAULT_EVENT_MAX_RETRIES = 2;
export const DEFAULT_EVENT_MIN_BACKOFF = 200;
export const DEFAULT_EVENT_MAX_BACKOFF = 1000;
export const INVALID_CACHE = 'Invalid cache';
export const INVALID_CACHE_METHOD = 'Invalid cache method %s';
const odpManagerSymbol = Symbol();
const validateCache = (cache) => {
    const errors = [];
    if (!cache || typeof cache !== 'object') {
        throw new Error(INVALID_CACHE);
    }
    for (const method of ['save', 'lookup', 'reset']) {
        if (typeof cache[method] !== 'function') {
            errors.push(INVALID_CACHE_METHOD.replace('%s', method));
        }
    }
    if (errors.length > 0) {
        throw new Error(errors.join(', '));
    }
};
const getDefaultSegmentsCache = (cacheSize, cacheTimeout) => {
    return new InMemoryLruCache(cacheSize || DEFAULT_CACHE_SIZE, cacheTimeout || DEFAULT_CACHE_TIMEOUT);
};
const getDefaultSegmentManager = (options) => {
    if (options.segmentsCache) {
        validateCache(options.segmentsCache);
    }
    return new DefaultOdpSegmentManager(options.segmentsCache || getDefaultSegmentsCache(options.segmentsCacheSize, options.segmentsCacheTimeout), new DefaultOdpSegmentApiManager(options.segmentRequestHandler));
};
const getDefaultEventManager = (options) => {
    return new DefaultOdpEventManager({
        apiManager: new DefaultOdpEventApiManager(options.eventRequestHandler, options.eventRequestGenerator),
        batchSize: options.eventBatchSize || DEFAULT_EVENT_BATCH_SIZE,
        repeater: new IntervalRepeater(options.eventFlushInterval || DEFAULT_EVENT_FLUSH_INTERVAL),
        retryConfig: {
            maxRetries: options.eventMaxRetries || DEFAULT_EVENT_MAX_RETRIES,
            backoffProvider: () => new ExponentialBackoff(options.eventMinBackoff || DEFAULT_EVENT_MIN_BACKOFF, options.eventMaxBackoff || DEFAULT_EVENT_MAX_BACKOFF, 50),
        },
    });
};
export const getOdpManager = (options) => {
    const segmentManager = getDefaultSegmentManager(options);
    const eventManager = getDefaultEventManager(options);
    return new DefaultOdpManager({
        segmentManager,
        eventManager,
        userAgentParser: options.userAgentParser,
    });
};
export const getOpaqueOdpManager = (options) => {
    return {
        [odpManagerSymbol]: getOdpManager(options),
    };
};
export const extractOdpManager = (manager) => {
    if (!manager || typeof manager !== 'object') {
        return undefined;
    }
    return manager[odpManagerSymbol];
};
export const __platforms = ['__universal__'];
//# sourceMappingURL=odp_manager_factory.js.map